import { useId, type ReactNode } from "react";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { TooltipContentProps } from "recharts";
import { useTranslation } from "react-i18next";
import { Award } from "lucide-react";
import type {
  CompetitorKpiChartPoint,
  CompetitorKpiCharts,
  CompetitorKpiMetric,
  DateWindow,
} from "@nexus/contracts";
import { formatBrandName, formatCountValue } from "../../lib/format";
import { formatCompetitorChartDate } from "../../lib/competitor-chart-date";
import { computeChartTitle } from "../../lib/periodBuckets";
import { CHART_AXIS_TICK, CHART_GRID_STROKE, KPI_CHART_HEIGHT } from "../../lib/kpi-chart-style";
import type { CompetitorKpiCardsState } from "../CompetitorKpiCards/CompetitorKpiCards";
import { BrandMark } from "../BrandMark/BrandMark";
import { KpiChartCard } from "../KpiChartCard/KpiChartCard";
import { KpiChartCardMessage } from "../KpiChartCard/KpiChartCardMessage";
import { KpiChartCardSkeleton } from "../KpiChartCard/KpiChartCardSkeleton";
import {
  CompetitorChartEndLabels,
  type CompetitorChartEndLabel,
} from "./CompetitorChartEndLabels";

type SeriesKey = "you" | "mainCompetitor" | "benchmark";

interface SeriesStyle {
  key: SeriesKey;
  color: string;
  width: number;
  cap: "round" | "butt";
  dash?: string;
  /** Tighter dash pattern so the short legend stroke still reads as dashed/dotted. */
  legendDash?: string;
}

/** Draw order: benchmark at the back, you on top. */
const SERIES: readonly SeriesStyle[] = [
  { key: "benchmark", color: "var(--chart-winners)", width: 2, cap: "round", dash: "0.5 5", legendDash: "0.5 4" },
  { key: "mainCompetitor", color: "var(--competitor)", width: 2, cap: "butt", dash: "7 5", legendDash: "5 3" },
  { key: "you", color: "var(--primary)", width: 2.5, cap: "round" },
];

const LEGEND_ORDER: readonly SeriesKey[] = ["you", "mainCompetitor", "benchmark"];

const AREA_TOP_OPACITY = 0.14;
const Y_AXIS_WIDTH = 44;
/** Right margin holding the end-of-line chips. */
const END_LABEL_AREA_WIDTH = 180;
const END_LABEL_LOGO_CLASS = "size-[18px] text-[10px]";

type ChartRow = CompetitorKpiChartPoint & { label: string };

interface CompetitorKpiChartProps {
  state: CompetitorKpiCardsState;
  charts?: CompetitorKpiCharts;
  metric: CompetitorKpiMetric;
  metricLabel: string;
  window: DateWindow;
  youLogoUrl?: string | null;
  competitorLogoUrl?: string | null;
}

export function CompetitorKpiChart({
  state,
  charts,
  metric,
  metricLabel,
  window,
  youLogoUrl,
  competitorLogoUrl,
}: CompetitorKpiChartProps) {
  const { t } = useTranslation();
  // React ids contain colons, which are CSS combinators and break `url(#...)`.
  const gradientId = `competitor-chart-area-${useId().replace(/:/g, "")}`;

  if (state === "blocked") {
    return (
      <KpiChartCardMessage>
        {t(
          "dashboard.competitorChart.selectionRequired",
          "Select your brand and main competitor to see data.",
        )}
      </KpiChartCardMessage>
    );
  }
  if (state === "error") {
    return <KpiChartCardMessage tone="error">{t("dashboard.competitorChart.error")}</KpiChartCardMessage>;
  }
  if (state === "loading" || (state === "ready" && !charts)) return <KpiChartCardSkeleton />;

  const points = charts?.[metric] ?? [];
  if (state === "empty" || points.length === 0) {
    return <KpiChartCardMessage>{t("dashboard.competitorChart.empty")}</KpiChartCardMessage>;
  }

  const youName = formatBrandName(charts?.brandLabels.you || t("dashboard.competitorChart.you"));
  const mainName = formatBrandName(
    charts?.brandLabels.mainCompetitor || t("dashboard.competitorChart.mainCompetitor"),
  );
  const seriesNames: Record<SeriesKey, string> = {
    you: youName,
    mainCompetitor: mainName,
    benchmark: t("dashboard.competitorChart.benchmark"),
  };
  const seriesLogos: Record<SeriesKey, ReactNode> = {
    you: <BrandMark label={youName} logoUrl={youLogoUrl} kind="you" className={END_LABEL_LOGO_CLASS} />,
    mainCompetitor: (
      <BrandMark label={mainName} logoUrl={competitorLogoUrl} kind="competitor" className={END_LABEL_LOGO_CLASS} />
    ),
    benchmark: (
      <span className="flex size-[18px] shrink-0 items-center justify-center rounded-full bg-[var(--slate-chip-bg)] text-[var(--slate-chip-text)]">
        <Award className="size-[11px]" aria-hidden />
      </span>
    ),
  };

  const hasMainCompetitor = points.some((point) => point.mainCompetitor > 0);
  const visibleSeries = SERIES.filter((series) => series.key !== "mainCompetitor" || hasMainCompetitor);
  const legendSeries = LEGEND_ORDER.flatMap((key) => visibleSeries.filter((series) => series.key === key));

  const chartData: ChartRow[] = points.map((point) => ({
    ...point,
    label: formatCompetitorChartDate(point.date),
  }));
  const lastRow = chartData[chartData.length - 1];
  const endLabels: CompetitorChartEndLabel[] = visibleSeries.map((series) => ({
    id: series.key,
    value: lastRow[series.key],
    valueLabel: formatCountValue(lastRow[series.key]),
    name: seriesNames[series.key],
    color: series.color,
    logo: seriesLogos[series.key],
  }));

  return (
    <KpiChartCard
      title={metricLabel}
      subtitle={t("dashboard.competitorChart.subtitle", { period: computeChartTitle(window) })}
      legend={<CompetitorChartLegend series={legendSeries} names={seriesNames} />}
    >
      <div data-competitor-kpi-chart>
        <ResponsiveContainer width="100%" height={KPI_CHART_HEIGHT}>
          <ComposedChart data={chartData} margin={{ top: 5, right: END_LABEL_AREA_WIDTH, bottom: 5, left: 0 }}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--primary)" stopOpacity={AREA_TOP_OPACITY} />
                <stop offset="100%" stopColor="var(--primary)" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_STROKE} vertical={false} />
            <XAxis
              dataKey="label"
              scale="point"
              tick={CHART_AXIS_TICK}
              tickLine={false}
              axisLine={false}
              interval="preserveStartEnd"
            />
            <YAxis
              tick={CHART_AXIS_TICK}
              tickLine={false}
              axisLine={false}
              width={Y_AXIS_WIDTH}
              tickFormatter={(value: number) => formatCountValue(value)}
            />
            <Tooltip
              content={(props) => (
                <TrendTooltip {...(props as TooltipContentProps<number, string>)} seriesNames={seriesNames} />
              )}
            />
            {visibleSeries.map((series) =>
              series.key === "you" ? (
                <Area
                  key={series.key}
                  type="monotone"
                  dataKey={series.key}
                  stroke={series.color}
                  strokeWidth={series.width}
                  strokeLinecap={series.cap}
                  fill={`url(#${gradientId})`}
                  fillOpacity={1}
                  dot={false}
                  connectNulls
                />
              ) : (
                <Line
                  key={series.key}
                  type="monotone"
                  dataKey={series.key}
                  stroke={series.color}
                  strokeWidth={series.width}
                  strokeLinecap={series.cap}
                  strokeDasharray={series.dash}
                  dot={false}
                  connectNulls
                />
              ),
            )}
            <CompetitorChartEndLabels
              labels={endLabels}
              lastCategory={lastRow.label}
              areaWidth={END_LABEL_AREA_WIDTH}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </KpiChartCard>
  );
}

function CompetitorChartLegend({
  series,
  names,
}: {
  series: readonly SeriesStyle[];
  names: Record<SeriesKey, string>;
}) {
  return (
    <div className="flex flex-wrap items-center gap-4">
      {series.map((item) => (
        <span key={item.key} className="flex items-center gap-1.5">
          <svg width="22" height="8" viewBox="0 0 22 8" className="shrink-0" aria-hidden>
            <line
              x1="2"
              y1="4"
              x2="20"
              y2="4"
              stroke={item.color}
              strokeWidth={item.width}
              strokeDasharray={item.legendDash}
              strokeLinecap={item.cap}
            />
          </svg>
          <span className="text-[12px] text-foreground/80">{names[item.key]}</span>
        </span>
      ))}
    </div>
  );
}

function TrendTooltip({
  active,
  payload,
  label,
  seriesNames,
}: TooltipContentProps<number, string> & { seriesNames: Record<SeriesKey, string> }) {
  if (!active || !payload?.length) return null;

  return (
    <div className="rounded-md border bg-card px-3 py-2 text-xs shadow-md">
      <p className="mb-1.5 font-medium text-foreground">{String(label)}</p>
      {payload.map((entry) => {
        const key = entry.dataKey as SeriesKey;
        return (
          <p key={key} className="tabular-nums text-muted-foreground">
            <span className="font-medium text-foreground">{seriesNames[key]}:</span>{" "}
            {formatCountValue(Number(entry.value ?? 0))}
          </p>
        );
      })}
    </div>
  );
}
